/**
 * Employer Benefits Management Page
 *
 * Allows employer administrators to:
 * - View all benefit programs and their enrollment status
 * - See all employee enrollments (pending, active, completed)
 * - Approve or decline pending enrollments
 * - View enrollment statistics and trends
 */

import { useState, useEffect } from 'react';
import {
  Gift,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Search,
  ChevronDown,
  AlertCircle,
  TrendingUp,
  Eye,
  X,
  Filter,
  Building2,
  Calendar,
  Hash,
  Mail,
  User,
  FileText,
  Target,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';

interface Employer {
  id: number;
  company_name: string;
}

interface Enrollment {
  id: number;
  enrollment_number: string | null;
  employee_id: number;
  employee_name: string;
  employee_email: string | null;
  program_id: number;
  program_name: string;
  program_category: string | null;
  enrollment_status: string;
  enrollment_date: string | null;
  enrolled_at: string | null;
  effective_date: string | null;
  end_date: string | null;
  enrollment_source: string | null;
  progress_percentage: number | null;
  outcome_status: string | null;
}

interface ProgramSummary {
  id: number;
  program_name: string;
  program_category: string | null;
  pending_count: number;
  active_count: number;
  total_count: number;
}

interface EnrollmentSummary {
  employer_id: number;
  employer_name: string;
  status_summary: Record<string, number>;
  total_enrollments: number;
  pending_enrollments: number;
  active_enrollments: number;
  programs: ProgramSummary[];
}

// All possible enrollment statuses
const ENROLLMENT_STATUSES = [
  { value: 'PENDING', label: 'Pending', description: 'Awaiting approval' },
  { value: 'ENROLLED', label: 'Enrolled', description: 'Approved and enrolled' },
  { value: 'ACTIVE', label: 'Active', description: 'Currently active' },
  { value: 'COMPLETED', label: 'Completed', description: 'Successfully completed' },
  { value: 'CANCELLED', label: 'Cancelled', description: 'Enrollment cancelled' },
  { value: 'DECLINED', label: 'Declined', description: 'Enrollment declined' },
];

export default function EmployerBenefitsManagementPage() {
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [selectedEmployer, setSelectedEmployer] = useState<Employer | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [summary, setSummary] = useState<EnrollmentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [programFilter, setProgramFilter] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingEnrollment, setUpdatingEnrollment] = useState<number | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId }),
    };
  };

  // Load employers on mount
  useEffect(() => {
    loadEmployers();
  }, []);

  // Load enrollments when employer is selected
  useEffect(() => {
    if (selectedEmployer) {
      loadEnrollmentData();
    }
  }, [selectedEmployer]);

  const loadEmployers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getWeaveinApiUrl('/api/employers'), {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setEmployers(data.employers || []);
        // Auto-select first employer if available
        if (data.employers?.length > 0 && !selectedEmployer) {
          setSelectedEmployer(data.employers[0]);
        }
      } else {
        setError('Failed to load employers');
      }
    } catch (err) {
      console.error('Error loading employers:', err);
      setError('Failed to load employers');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollmentData = async () => {
    if (!selectedEmployer) return;

    try {
      setEnrollmentsLoading(true);
      setError(null);

      // Load summary and enrollments in parallel
      const [summaryRes, enrollmentsRes] = await Promise.all([
        fetch(getWeaveinApiUrl(`/api/employers/${selectedEmployer.id}/enrollments/summary`), {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
        fetch(getWeaveinApiUrl(`/api/employers/${selectedEmployer.id}/enrollments`), {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
      ]);

      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }

      if (enrollmentsRes.ok) {
        const enrollmentsData = await enrollmentsRes.json();
        setEnrollments(enrollmentsData);
      }
    } catch (err) {
      console.error('Error loading enrollment data:', err);
      setError('Failed to load enrollment data');
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const updateEnrollmentStatus = async (enrollmentId: number, newStatus: string) => {
    if (!selectedEmployer) return;

    try {
      setUpdatingEnrollment(enrollmentId);
      setError(null); // Clear any previous errors

      const url = getWeaveinApiUrl(`/api/employers/${selectedEmployer.id}/enrollments/${enrollmentId}/status`);
      console.log('Updating enrollment status:', { enrollmentId, newStatus, url });

      const response = await fetch(url, {
        method: 'PATCH',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        // Update local state immediately
        setEnrollments((prev) =>
          prev.map((e) =>
            e.id === enrollmentId ? { ...e, enrollment_status: newStatus } : e
          )
        );
        // Refresh summary to get updated counts
        loadEnrollmentData();
      } else {
        let errorMessage = 'Failed to update enrollment status';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = `Error: ${response.status} ${response.statusText}`;
        }
        console.error('Update failed:', errorMessage);
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error updating enrollment:', err);
      setError(`Failed to update enrollment: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUpdatingEnrollment(null);
    }
  };

  // Filter enrollments
  const filteredEnrollments = enrollments.filter((enrollment) => {
    // Status filter
    if (statusFilter !== 'all' && enrollment.enrollment_status !== statusFilter) {
      return false;
    }

    // Program filter
    if (programFilter && enrollment.program_id !== programFilter) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        enrollment.employee_name.toLowerCase().includes(term) ||
        enrollment.employee_email?.toLowerCase().includes(term) ||
        enrollment.program_name.toLowerCase().includes(term) ||
        enrollment.enrollment_number?.toLowerCase().includes(term)
      );
    }

    return true;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'ENROLLED':
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CANCELLED':
      case 'DECLINED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-paper-2 text-ink-700 border-v2border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-3 h-3" />;
      case 'ENROLLED':
      case 'ACTIVE':
        return <CheckCircle className="w-3 h-3" />;
      case 'COMPLETED':
        return <CheckCircle className="w-3 h-3" />;
      case 'CANCELLED':
      case 'DECLINED':
        return <XCircle className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const openEnrollmentDetails = (enrollment: Enrollment) => {
    setSelectedEnrollment(enrollment);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedEnrollment(null);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const header = (
    <PageHeader
      eyebrow="Admin · Benefits"
      title="Benefits Management"
      description="View and manage employee benefit enrollments across your organization."
    />
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState variant="loading" title="Loading employers..." />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {header}
      <PageContainer>
        {/* Employer Selector */}
        <div className="bg-surface rounded-xl shadow-sm border border-v2border p-4 mb-6">
          <div className="flex items-center gap-4">
            <Building2 className="w-5 h-5 text-ink-500" />
            <div className="flex-1">
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Select Employer
              </label>
              <div className="relative">
                <select
                  value={selectedEmployer?.id || ''}
                  onChange={(e) => {
                    const emp = employers.find((em) => em.id === Number(e.target.value));
                    setSelectedEmployer(emp || null);
                  }}
                  className="w-full appearance-none px-4 py-2 pr-10 border border-v2border rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select an employer...</option>
                  {employers.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.company_name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              </div>
            </div>
            <button
              onClick={loadEnrollmentData}
              disabled={!selectedEmployer || enrollmentsLoading}
              className="px-4 py-2 bg-paper-2 text-ink-700 rounded-lg hover:bg-paper-2 transition-colors disabled:opacity-50 mt-6"
            >
              <RefreshCw className={`w-4 h-4 ${enrollmentsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {selectedEmployer && summary && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-surface rounded-xl shadow-sm border border-v2border p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-ink-500">Total Enrollments</span>
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="font-display text-2xl font-semibold text-ink">{summary.total_enrollments}</p>
              </div>

              <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-amber-700">Pending Approval</span>
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <p className="font-display text-2xl font-semibold text-amber-800">{summary.pending_enrollments}</p>
              </div>

              <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-700">Active</span>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="font-display text-2xl font-semibold text-green-800">{summary.active_enrollments}</p>
              </div>

              <div className="bg-surface rounded-xl shadow-sm border border-v2border p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-ink-500">Programs</span>
                  <Gift className="w-5 h-5 text-ink-400" />
                </div>
                <p className="font-display text-2xl font-semibold text-ink">{summary.programs.length}</p>
              </div>
            </div>

            {/* Programs Overview */}
            {summary.programs.length > 0 && (
              <div className="bg-surface rounded-xl shadow-sm border border-v2border p-6 mb-6">
                <h2 className="text-lg font-semibold text-ink mb-4">Programs Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {summary.programs.map((program) => (
                    <div
                      key={program.id}
                      onClick={() => setProgramFilter(programFilter === program.id ? null : program.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        programFilter === program.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-v2border hover:border-indigo-300'
                      }`}
                    >
                      <h3 className="font-medium text-ink mb-1">{program.program_name}</h3>
                      <p className="text-sm text-ink-500 mb-2">
                        {program.program_category?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'Uncategorized'}
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-amber-600">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {program.pending_count} pending
                        </span>
                        <span className="text-green-600">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {program.active_count} active
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-surface rounded-xl shadow-sm border border-v2border p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <input
                    type="text"
                    placeholder="Search by employee name, email, or enrollment..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-ink-500" />
                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="appearance-none px-4 py-2 pr-10 border border-v2border rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="ENROLLED">Enrolled</option>
                      <option value="ACTIVE">Active</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="DECLINED">Declined</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  </div>
                </div>
                {programFilter && (
                  <button
                    onClick={() => setProgramFilter(null)}
                    className="px-3 py-2 text-sm text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                  >
                    Clear Program Filter
                  </button>
                )}
              </div>
            </div>

            {/* Enrollments Table */}
            <div className="bg-surface rounded-xl shadow-sm border border-v2border overflow-hidden">
              <div className="px-6 py-4 border-b border-v2border">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-ink">
                    Employee Enrollments
                    <span className="ml-2 text-sm font-normal text-ink-500">
                      ({filteredEnrollments.length} {filteredEnrollments.length === 1 ? 'enrollment' : 'enrollments'})
                    </span>
                  </h2>
                </div>
              </div>

              {enrollmentsLoading ? (
                <div className="p-12 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
                  <p className="text-ink-500">Loading enrollments...</p>
                </div>
              ) : filteredEnrollments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-v2border-muted">
                    <thead className="bg-paper">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Employee
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Program
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Enrolled
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Effective
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Progress
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-v2border-muted">
                      {filteredEnrollments.map((enrollment) => (
                        <tr key={enrollment.id} className="hover:bg-paper">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <p className="text-sm font-medium text-ink">
                                {enrollment.employee_name}
                              </p>
                              {enrollment.employee_email && (
                                <p className="text-sm text-ink-500">{enrollment.employee_email}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <p className="text-sm font-medium text-ink">
                                {enrollment.program_name}
                              </p>
                              {enrollment.program_category && (
                                <p className="text-xs text-ink-500">
                                  {enrollment.program_category.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(
                                enrollment.enrollment_status
                              )}`}
                            >
                              {getStatusIcon(enrollment.enrollment_status)}
                              {enrollment.enrollment_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                            {formatDate(enrollment.enrolled_at || enrollment.enrollment_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                            {formatDate(enrollment.effective_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {enrollment.progress_percentage !== null && enrollment.progress_percentage > 0 ? (
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-paper-2 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      enrollment.progress_percentage >= 100
                                        ? 'bg-green-500'
                                        : enrollment.progress_percentage >= 50
                                          ? 'bg-blue-500'
                                          : 'bg-amber-500'
                                    }`}
                                    style={{ width: `${Math.min(enrollment.progress_percentage, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-ink-500">{enrollment.progress_percentage}%</span>
                              </div>
                            ) : (
                              <span className="text-xs text-ink-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex items-center justify-end gap-2">
                              {/* Status Dropdown */}
                              <div className="relative">
                                <select
                                  value={enrollment.enrollment_status?.toUpperCase() || 'PENDING'}
                                  onChange={(e) => {
                                    const newStatus = e.target.value;
                                    if (newStatus !== (enrollment.enrollment_status?.toUpperCase() || 'PENDING')) {
                                      updateEnrollmentStatus(enrollment.id, newStatus);
                                    }
                                  }}
                                  disabled={updatingEnrollment === enrollment.id}
                                  className={`appearance-none px-3 py-1.5 pr-8 text-sm border rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                                    updatingEnrollment === enrollment.id
                                      ? 'border-indigo-400 animate-pulse'
                                      : 'border-v2border'
                                  }`}
                                  title={`Current: ${enrollment.enrollment_status || 'Unknown'} - Click to change`}
                                >
                                  {ENROLLMENT_STATUSES.map((status) => (
                                    <option key={status.value} value={status.value}>
                                      {status.label}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                                {updatingEnrollment === enrollment.id && (
                                  <RefreshCw className="absolute -right-6 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 animate-spin" />
                                )}
                              </div>

                              {/* View Details */}
                              <button
                                onClick={() => openEnrollmentDetails(enrollment)}
                                className="px-3 py-1.5 bg-paper-2 text-ink-700 rounded-lg hover:bg-paper-2 transition-colors flex items-center gap-1"
                                title="View details"
                              >
                                <Eye className="w-4 h-4" />
                                Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-ink-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-ink mb-1">No Enrollments Found</h3>
                  <p className="text-ink-500">
                    {searchTerm || statusFilter !== 'all' || programFilter
                      ? 'Try adjusting your filters to see more results.'
                      : 'No employees have enrolled in benefit programs yet.'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {selectedEmployer && !summary && !enrollmentsLoading && (
          <div className="bg-surface rounded-xl shadow-sm border border-v2border p-12 text-center">
            <Gift className="w-12 h-12 text-ink-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-ink mb-1">No Data Available</h3>
            <p className="text-ink-500">
              No enrollment data is available for this employer yet.
            </p>
          </div>
        )}

        {!selectedEmployer && (
          <div className="bg-surface rounded-xl shadow-sm border border-v2border p-12 text-center">
            <Building2 className="w-12 h-12 text-ink-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-ink mb-1">Select an Employer</h3>
            <p className="text-ink-500">
              Please select an employer above to view and manage benefit enrollments.
            </p>
          </div>
        )}
      </PageContainer>

      {/* Enrollment Detail Modal */}
      {showDetailModal && selectedEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-surface border-b border-v2border px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-ink">Enrollment Details</h2>
                    <p className="text-sm text-ink-500">
                      {selectedEnrollment.enrollment_number || `ID: ${selectedEnrollment.id}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDetailModal}
                  className="p-2 text-ink-400 hover:text-ink-500 hover:bg-paper-2 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-center">
                <span
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${getStatusBadgeClass(
                    selectedEnrollment.enrollment_status
                  )}`}
                >
                  {getStatusIcon(selectedEnrollment.enrollment_status)}
                  {selectedEnrollment.enrollment_status}
                </span>
              </div>

              {/* Employee Information */}
              <div className="bg-paper rounded-xl p-4">
                <h3 className="text-sm font-medium text-ink-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Employee Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-ink-500 mb-1">Name</p>
                    <p className="text-sm font-medium text-ink">{selectedEnrollment.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-500 mb-1">Email</p>
                    <p className="text-sm font-medium text-ink flex items-center gap-1">
                      {selectedEnrollment.employee_email ? (
                        <>
                          <Mail className="w-3 h-3 text-ink-400" />
                          {selectedEnrollment.employee_email}
                        </>
                      ) : (
                        '-'
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-500 mb-1">Employee ID</p>
                    <p className="text-sm font-medium text-ink">{selectedEnrollment.employee_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-500 mb-1">Enrollment Source</p>
                    <p className="text-sm font-medium text-ink">
                      {selectedEnrollment.enrollment_source?.replace('_', ' ') || 'Direct Enrollment'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Program Information */}
              <div className="bg-indigo-50 rounded-xl p-4">
                <h3 className="text-sm font-medium text-indigo-700 mb-3 flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  Program Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-indigo-600 mb-1">Program Name</p>
                    <p className="text-sm font-medium text-ink">{selectedEnrollment.program_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-600 mb-1">Category</p>
                    <p className="text-sm font-medium text-ink">
                      {selectedEnrollment.program_category?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'Uncategorized'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dates & Progress */}
              <div className="bg-surface border border-v2border rounded-xl p-4">
                <h3 className="text-sm font-medium text-ink-700 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Dates & Progress
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-ink-500 mb-1">Enrollment Date</p>
                    <p className="text-sm font-medium text-ink">
                      {formatDate(selectedEnrollment.enrolled_at || selectedEnrollment.enrollment_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-500 mb-1">Effective Date</p>
                    <p className="text-sm font-medium text-ink">
                      {formatDate(selectedEnrollment.effective_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-500 mb-1">End Date</p>
                    <p className="text-sm font-medium text-ink">
                      {formatDate(selectedEnrollment.end_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-500 mb-1">Outcome Status</p>
                    <p className="text-sm font-medium text-ink">
                      {selectedEnrollment.outcome_status || '-'}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                {selectedEnrollment.progress_percentage !== null && selectedEnrollment.progress_percentage > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-ink-500 flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Progress
                      </span>
                      <span className="text-xs font-medium text-ink-700">
                        {selectedEnrollment.progress_percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-paper-2 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all"
                        style={{ width: `${selectedEnrollment.progress_percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Enrollment ID */}
              <div className="flex items-center justify-between p-3 bg-paper-2 rounded-lg">
                <span className="text-xs text-ink-500 flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  Enrollment ID
                </span>
                <span className="text-sm font-mono text-ink-700">{selectedEnrollment.id}</span>
              </div>
            </div>

            {/* Change Status Section */}
            <div className="px-6 pb-4">
              <div className="bg-paper rounded-xl p-4">
                <h3 className="text-sm font-medium text-ink-700 mb-3 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Change Enrollment Status
                </h3>
                <div className="relative">
                  <select
                    value={selectedEnrollment.enrollment_status?.toUpperCase() || 'PENDING'}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      if (newStatus !== (selectedEnrollment.enrollment_status?.toUpperCase() || 'PENDING')) {
                        updateEnrollmentStatus(selectedEnrollment.id, newStatus);
                        // Update the local selected enrollment to show new status
                        setSelectedEnrollment({
                          ...selectedEnrollment,
                          enrollment_status: newStatus,
                        });
                      }
                    }}
                    disabled={updatingEnrollment === selectedEnrollment.id}
                    className="w-full appearance-none px-4 py-3 pr-10 border border-v2border rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {ENROLLMENT_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label} - {status.description}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400 pointer-events-none" />
                </div>
                {updatingEnrollment === selectedEnrollment.id && (
                  <p className="mt-2 text-sm text-indigo-600 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Updating status...
                  </p>
                )}
                <p className="mt-2 text-xs text-ink-500">
                  Select a new status from the dropdown to update this enrollment.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-paper border-t border-v2border px-6 py-4 rounded-b-2xl">
              <div className="flex items-center justify-end">
                <button
                  onClick={closeDetailModal}
                  className="px-4 py-2 text-ink-500 bg-surface border border-v2border rounded-lg hover:bg-paper transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
